import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DeleteIcon from '@mui/icons-material/Delete'

const QUOTES_COLLAPSED_LIMIT = 3
import { useCustomMutation, useNotification } from '@refinedev/core'
import type {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'
import {
  validateCloseDate,
  validateOpenDate,
  validateRound,
} from '@/utils/fundingRoundValidation'
import { EMAIL_REGEX } from '@/utils/validation'

// ────────────────────────────────────────────────────────────────────────
// Backend contract (mirrors app/models/opportunity_draft_models.py)
// ────────────────────────────────────────────────────────────────────────

interface SourceQuote {
  text: string
  source_name: string
}

interface CollatedField<T> {
  value: T
  quotes: SourceQuote[]
}

interface FailedSource {
  name: string
  status: 'blocked' | 'failure'
  error: string
}

interface CollatedFundingRound {
  application_open_date: string | null
  application_close_date: string | null
  decision_date: string | null
  quotes: SourceQuote[]
}

export interface DraftFundingRoundShape {
  application_open_date: string | null
  application_close_date: string | null
  decision_date: string | null
}

interface CollatedDraft {
  fund_name: CollatedField<string> | null
  description: CollatedField<string> | null
  criteria: CollatedField<string> | null
  exclusions: CollatedField<string> | null
  general_info: CollatedField<string> | null
  application_info: CollatedField<string> | null
  timing: CollatedField<string> | null
  amount: CollatedField<string> | null
  min_amount: CollatedField<number> | null
  max_amount: CollatedField<number> | null
  no_min_max_amount: CollatedField<boolean> | null
  always_open: CollatedField<boolean> | null
  opportunity_link: CollatedField<string> | null
  application_link: CollatedField<string> | null
  email_address: CollatedField<string> | null
  gambling_proceeds: CollatedField<boolean> | null
  is_international: CollatedField<boolean> | null
  only_funds_registered_charities: CollatedField<boolean> | null
  is_relational: CollatedField<boolean> | null
}

interface OpportunityDraftResponse {
  fields: CollatedDraft
  proposed_rounds: CollatedFundingRound[]
  confidence: number
  parsing_notes: string
  ambiguities: string[]
  warnings: string[]
  sources: string[]
  failed_sources: FailedSource[]
}

// Names match the backend field keys AND the react-hook-form field paths.
type InScopeFieldName = keyof CollatedDraft

type FieldKind = 'text' | 'longtext' | 'number' | 'boolean' | 'url'

interface FieldSpec {
  name: InScopeFieldName
  label: string
  kind: FieldKind
  rows?: number
}

// Order mirrors the create.tsx form layout so reviewers see fields in the
// same sequence they'll appear on the form.
const FIELD_SPECS: FieldSpec[] = [
  { name: 'fund_name', label: 'Fund Name', kind: 'text' },
  { name: 'opportunity_link', label: 'Opportunity Link', kind: 'url' },
  // Hard filters
  { name: 'gambling_proceeds', label: 'Gambling Proceeds', kind: 'boolean' },
  { name: 'is_international', label: 'Is International', kind: 'boolean' },
  {
    name: 'only_funds_registered_charities',
    label: 'Only funds registered charities',
    kind: 'boolean',
  },
  { name: 'is_relational', label: 'Is Relational', kind: 'boolean' },
  // Amount details
  { name: 'amount', label: 'Amount Description', kind: 'longtext' },
  { name: 'min_amount', label: 'Minimum Amount', kind: 'number' },
  { name: 'max_amount', label: 'Maximum Amount', kind: 'number' },
  { name: 'no_min_max_amount', label: 'No min/max amount', kind: 'boolean' },
  // Timing details (proposed_rounds renders directly after timing)
  { name: 'always_open', label: 'Always Open', kind: 'boolean' },
  { name: 'timing', label: 'Timing', kind: 'longtext' },
  // Content fields
  { name: 'description', label: 'Description', kind: 'longtext', rows: 8 },
  { name: 'criteria', label: 'Criteria', kind: 'longtext', rows: 8 },
  { name: 'exclusions', label: 'Exclusions', kind: 'longtext', rows: 8 },
  { name: 'general_info', label: 'General Info', kind: 'longtext', rows: 8 },
  {
    name: 'application_info',
    label: 'Application Info',
    kind: 'longtext',
    rows: 8,
  },
  { name: 'application_link', label: 'Application Link', kind: 'url' },
  { name: 'email_address', label: 'Email Address', kind: 'text' },
]

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

const parseUrlLines = (value: string): string[] =>
  value
    .split(/\r?\n/)
    .map((u) => u.trim())
    .filter(Boolean)

const buildExistingFields = (
  getValues: UseFormGetValues<FieldValues>,
): Record<string, unknown> => {
  const snapshot: Record<string, unknown> = {}
  for (const spec of FIELD_SPECS) {
    const v = getValues(spec.name)
    if (v !== undefined && v !== '' && v !== null) {
      snapshot[spec.name] = v
    }
  }
  return snapshot
}

const isPopulated = (v: unknown): boolean =>
  v !== undefined && v !== null && v !== '' && v !== false

// Map of backend field name → human-readable label. Used to rewrite raw
// field names in LLM-emitted strings (parsing_notes, ambiguities, failed
// source errors) before display. Includes proposed_rounds even though it
// isn't a FieldSpec, since the LLM may reference it in ambiguities.
const FIELD_LABEL_MAP: Record<string, string> = {
  ...Object.fromEntries(FIELD_SPECS.map((s) => [s.name, s.label])),
  proposed_rounds: 'Funding Rounds',
}

// Replace `\bfield_name\b` occurrences with their human label. Word
// boundary anchors keep us from mangling text that happens to contain a
// field name as a substring of a longer identifier.
const humanizeFieldRefs = (text: string): string => {
  if (!text) return text
  let out = text
  for (const [key, label] of Object.entries(FIELD_LABEL_MAP)) {
    out = out.replace(new RegExp(`\\b${key}\\b`, 'g'), label)
  }
  return out
}

// Header fields where appending would produce an invalid value (URLs)
// or just look weird (fund_name). On Apply these overwrite existing
// content rather than appending with a separator.
const isHeaderField = (spec: FieldSpec): boolean =>
  spec.name === 'fund_name' ||
  spec.name === 'email_address' ||
  spec.kind === 'url'


const applyField = (
  spec: FieldSpec,
  value: unknown,
  currentValue: unknown,
  setValue: UseFormSetValue<FieldValues>,
): void => {
  const name = spec.name
  // Skip null/undefined entirely — never overwrite existing form values
  // with the absence of LLM evidence.
  if (value == null) return

  if (spec.kind === 'longtext' || spec.kind === 'text' || spec.kind === 'url') {
    const proposed = String(value).trim()
    if (!proposed) return
    const current = typeof currentValue === 'string' ? currentValue.trim() : ''
    const merged =
      current && !isHeaderField(spec) ? `${currentValue}\n\n${proposed}` : proposed
    setValue(name, merged as never, { shouldDirty: true })
    return
  }
  if (spec.kind === 'number') {
    // min_amount / max_amount are stored as string | null on the form.
    setValue(name, String(value) as never, { shouldDirty: true })
    return
  }
  setValue(name, Boolean(value) as never, { shouldDirty: true })
}

// ────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────

export interface OpportunityAutoFillDialogProps {
  open: boolean
  onClose: () => void
  initialUrls: string[]
  getValues: UseFormGetValues<FieldValues>
  setValue: UseFormSetValue<FieldValues>
  /**
   * Called on Apply with the rounds the admin toggled on. The parent is
   * expected to append them to its draft-mode FundingRoundManagement.
   */
  onApplyRounds?: (rounds: DraftFundingRoundShape[]) => void
  /**
   * When true and ``initialUrls`` is non-empty, automatically fires Extract
   * once on dialog open instead of waiting for the admin to click. Used
   * when the dialog is opened by the funder-page "Add with references"
   * shortcut. Empty initialUrls + autoExtract still leaves the dialog in
   * input state so the admin can paste manually.
   */
  autoExtract?: boolean
}

type Phase = 'input' | 'pending' | 'review'

export default function OpportunityAutoFillDialog({
  open,
  onClose,
  initialUrls,
  getValues,
  setValue,
  onApplyRounds,
  autoExtract,
}: OpportunityAutoFillDialogProps) {
  const [phase, setPhase] = useState<Phase>('input')
  const [urlsText, setUrlsText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [draft, setDraft] = useState<OpportunityDraftResponse | null>(null)
  const [emptyNotice, setEmptyNotice] = useState('')
  const [applyToggles, setApplyToggles] = useState<Record<string, boolean>>({})
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({})
  const [quotesExpanded, setQuotesExpanded] = useState<Record<string, boolean>>(
    {},
  )
  const [roundToggles, setRoundToggles] = useState<Record<number, boolean>>({})
  const [editedRounds, setEditedRounds] = useState<
    Record<number, DraftFundingRoundShape>
  >({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Guards `autoExtract` so it fires at most once per dialog open cycle.
  // Reset to false in the open-seed effect; flipped to true in the
  // auto-fire effect once it dispatches.
  const autoExtractFiredRef = useRef(false)

  const { open: notify } = useNotification()
  const { mutate: runExtract } = useCustomMutation<OpportunityDraftResponse>()

  // Pre-populate URLs from the References field on open. We intentionally
  // only seed on open — edits to the References field while the dialog is
  // open would be surprising to clobber.
  useEffect(() => {
    if (open) {
      setUrlsText(initialUrls.join('\n'))
      setFiles([])
      setDraft(null)
      setEmptyNotice('')
      setApplyToggles({})
      setEditedValues({})
      setQuotesExpanded({})
      setRoundToggles({})
      setEditedRounds({})
      setPhase('input')
      autoExtractFiredRef.current = false
    }
  }, [open, initialUrls])

  const urls = useMemo(() => parseUrlLines(urlsText), [urlsText])
  const sourceCount = urls.length + files.length
  const canExtract = sourceCount > 0 && phase !== 'pending'

  // One-shot auto-fire: when caller passed `autoExtract` and the dialog
  // has opened with non-empty initialUrls already seeded into `urls`, fire
  // Extract automatically. Empty URL list leaves the admin in input state.
  useEffect(() => {
    if (
      !open ||
      !autoExtract ||
      autoExtractFiredRef.current ||
      phase !== 'input' ||
      urls.length === 0
    ) {
      return
    }
    autoExtractFiredRef.current = true
    handleExtract()
    // handleExtract is intentionally omitted — it's recreated each render
    // and the ref guard above prevents re-fires within an open cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoExtract, phase, urls.length])

  const populatedFields = useMemo(() => {
    if (!draft) return []
    return FIELD_SPECS.filter((spec) => draft.fields[spec.name] != null)
  }, [draft])

  // True when at least one round the admin has Apply-toggled on has invalid
  // dates per the shared funding-round validation rules. Used to block the
  // dialog's "Apply to form" button.
  const hasInvalidActiveRound = useMemo(() => {
    if (!draft) return false
    return draft.proposed_rounds.some((round, idx) => {
      const on = roundToggles[idx] ?? true
      if (!on) return false
      const edited = editedRounds[idx] ?? {
        application_open_date: round.application_open_date,
        application_close_date: round.application_close_date,
        decision_date: round.decision_date,
      }
      const result = validateRound({
        application_open_date: edited.application_open_date
          ? new Date(edited.application_open_date)
          : null,
        application_close_date: edited.application_close_date
          ? new Date(edited.application_close_date)
          : null,
        decision_date: edited.decision_date
          ? new Date(edited.decision_date)
          : null,
      })
      return !result.isValid
    })
  }, [draft, roundToggles, editedRounds])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files ?? [])
    setFiles((prev) => [...prev, ...incoming])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleExtract = () => {
    const formData = new FormData()
    urls.forEach((url) => formData.append('urls', url))
    files.forEach((f) => formData.append('files', f))
    formData.append(
      'existing_fields',
      JSON.stringify(buildExistingFields(getValues)),
    )

    setEmptyNotice('')
    setPhase('pending')

    runExtract(
      {
        url: '/opportunities/extract-draft',
        method: 'post',
        values: formData,
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
      },
      {
        onSuccess: (response) => {
          const data = response.data
          if (!data) {
            setPhase('input')
            setEmptyNotice('Empty response from server. Please try again.')
            return
          }
          const populatedFieldsExist = FIELD_SPECS.some(
            (spec) => data.fields[spec.name] != null,
          )
          const hasRounds = (data.proposed_rounds ?? []).length > 0
          if (!populatedFieldsExist && !hasRounds) {
            setPhase('input')
            setEmptyNotice(
              'No fields could be extracted from these sources. Try different URLs or upload a file instead.',
            )
            setDraft(data)
            return
          }
          setDraft(data)
          const initialToggles: Record<string, boolean> = {}
          const initialEdits: Record<string, unknown> = {}
          for (const spec of FIELD_SPECS) {
            const field = data.fields[spec.name]
            if (field != null) {
              initialToggles[spec.name] = true
              initialEdits[spec.name] = field.value
            }
          }
          const initialRoundToggles: Record<number, boolean> = {}
          const initialRoundEdits: Record<number, DraftFundingRoundShape> = {}
          ;(data.proposed_rounds ?? []).forEach((r, idx) => {
            initialRoundToggles[idx] = true
            initialRoundEdits[idx] = {
              application_open_date: r.application_open_date,
              application_close_date: r.application_close_date,
              decision_date: r.decision_date,
            }
          })
          setApplyToggles(initialToggles)
          setEditedValues(initialEdits)
          setRoundToggles(initialRoundToggles)
          setEditedRounds(initialRoundEdits)
          setPhase('review')
        },
        onError: (err) => {
          setPhase('input')
          const msg =
            (
              err as {
                message?: string
                response?: { data?: { detail?: string } }
              }
            )?.response?.data?.detail ??
            (err as { message?: string })?.message ??
            'Extraction failed.'
          notify?.({
            type: 'error',
            message: 'Auto-fill failed',
            description: msg,
          })
        },
      },
    )
  }

  const handleApply = () => {
    if (!draft) return
    for (const spec of FIELD_SPECS) {
      const on = applyToggles[spec.name]
      if (!on) continue
      const edited = editedValues[spec.name]
      const current = getValues(spec.name)
      if (spec.name === 'email_address' && typeof edited === 'string') {
        const trimmed = edited.trim()
        if (trimmed && !EMAIL_REGEX.test(trimmed)) {
          notify?.({
            type: 'error',
            message: 'Skipped invalid email',
            description: `"${trimmed}" is not a valid email address.`,
          })
          continue
        }
      }
      applyField(spec, edited, current, setValue)
    }
    if (onApplyRounds) {
      const acceptedRounds = (draft.proposed_rounds ?? [])
        .map((r, idx) => ({ round: r, idx }))
        .filter(({ idx }) => roundToggles[idx])
        .map(
          ({ round, idx }) =>
            editedRounds[idx] ?? {
              application_open_date: round.application_open_date,
              application_close_date: round.application_close_date,
              decision_date: round.decision_date,
            },
        )
      if (acceptedRounds.length > 0) {
        onApplyRounds(acceptedRounds)
      }
    }
    notify?.({ type: 'success', message: 'Fields applied to form' })
    onClose()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Render helpers
  // ──────────────────────────────────────────────────────────────────────

  const renderInputState = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Paste one URL per line (pre-populated from the References field) and
        optionally add PDF or DOCX files. The drafter will read each source and
        propose values for review before anything lands on the form.
      </Typography>
      <TextField
        label="URLs (one per line)"
        value={urlsText}
        onChange={(e) => setUrlsText(e.target.value)}
        multiline
        rows={5}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <Box>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Add files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          multiple
          hidden
          onChange={handleFileChange}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {files.map((f, idx) => (
            <Chip
              key={`${f.name}-${idx}`}
              label={f.name}
              onDelete={() => handleRemoveFile(idx)}
              deleteIcon={<DeleteIcon />}
            />
          ))}
        </Stack>
      </Box>
      {emptyNotice && <Alert severity="info">{emptyNotice}</Alert>}
      <Typography variant="body2" color="text.secondary">
        {sourceCount === 0
          ? 'Add at least one URL or file to begin.'
          : `${sourceCount} source${sourceCount === 1 ? '' : 's'} ready.`}
      </Typography>
    </Stack>
  )

  const renderPendingState = () => (
    <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
      <CircularProgress />
      <Typography>
        Extracting details from {sourceCount} source
        {sourceCount === 1 ? '' : 's'}…
      </Typography>
    </Stack>
  )

  const renderSingleQuote = (q: SourceQuote, key: number | string) => (
    <Box key={key} sx={{ mb: 0.75 }}>
      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
        “{q.text}”
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        from{' '}
        {/^https?:\/\//.test(q.source_name) ? (
          <Link href={q.source_name} target="_blank" rel="noreferrer">
            {q.source_name}
          </Link>
        ) : (
          q.source_name
        )}
      </Typography>
    </Box>
  )

  const renderQuoteList = (
    quotes: SourceQuote[],
    expanded: boolean,
    fieldName: string,
  ) => {
    const visible = expanded ? quotes : quotes.slice(0, QUOTES_COLLAPSED_LIMIT)
    const hiddenCount = quotes.length - QUOTES_COLLAPSED_LIMIT
    return (
      <>
        {visible.map((q, idx) => renderSingleQuote(q, idx))}
        {hiddenCount > 0 && (
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: 'none', pl: 0, minWidth: 0, mt: 0.5 }}
            onClick={() =>
              setQuotesExpanded((prev) => ({
                ...prev,
                [fieldName]: !expanded,
              }))
            }
          >
            {expanded ? 'Show less' : `Show ${hiddenCount} more`}
          </Button>
        )}
      </>
    )
  }

  const renderFieldRow = (spec: FieldSpec) => {
    if (!draft) return null
    const field = draft.fields[spec.name]
    if (field == null) return null

    const toggledOn = applyToggles[spec.name] ?? true
    const editedValue = editedValues[spec.name] ?? field.value
    const currentForm = getValues(spec.name)
    const existingHasContent = isPopulated(currentForm)

    const updateEditedValue = (v: unknown) =>
      setEditedValues((prev) => ({ ...prev, [spec.name]: v }))

    const inputControl = (() => {
      if (spec.kind === 'boolean') {
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(editedValue)}
                onChange={(e) => updateEditedValue(e.target.checked)}
              />
            }
            label={spec.label}
          />
        )
      }
      const isMultiline = spec.kind === 'longtext'
      return (
        <TextField
          label={spec.label}
          value={editedValue ?? ''}
          onChange={(e) => updateEditedValue(e.target.value)}
          fullWidth
          multiline={isMultiline}
          rows={isMultiline ? spec.rows ?? 4 : 1}
          InputLabelProps={{ shrink: true }}
          type={spec.kind === 'number' ? 'number' : 'text'}
          sx={
            isMultiline
              ? { '& textarea': { resize: 'vertical', minHeight: '1em' } }
              : undefined
          }
        />
      )
    })()

    return (
      <Box
        key={spec.name}
        sx={{
          opacity: toggledOn ? 1 : 0.5,
          borderLeft: (t) =>
            `3px solid ${
              toggledOn ? t.palette.primary.main : t.palette.divider
            }`,
          pl: 2,
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Checkbox
            checked={toggledOn}
            onChange={(e) =>
              setApplyToggles((prev) => ({
                ...prev,
                [spec.name]: e.target.checked,
              }))
            }
            inputProps={{ 'aria-label': `Apply ${spec.label}` }}
          />
          <Box sx={{ flex: 1 }}>{inputControl}</Box>
        </Stack>
        {existingHasContent && (
          <Typography variant="caption" color="text.secondary">
            {spec.kind === 'text' ||
            spec.kind === 'longtext' ||
            spec.kind === 'url'
              ? isHeaderField(spec)
                ? 'Will overwrite existing content.'
                : 'Will be appended to existing content.'
              : `Current: ${JSON.stringify(
                  currentForm,
                )} → proposed: ${JSON.stringify(editedValue)}`}
          </Typography>
        )}
        {field.quotes.length > 0 && (
          <Box
            sx={{
              mt: 1,
              pl: 1.5,
              borderLeft: (t) => `2px solid ${t.palette.divider}`,
            }}
          >
            {renderQuoteList(
              field.quotes,
              quotesExpanded[spec.name] ?? false,
              spec.name,
            )}
          </Box>
        )}
      </Box>
    )
  }

  const renderReviewHeader = () => {
    if (!draft) return null
    return (
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Confidence: {(draft.confidence * 100).toFixed(0)}% — toggle fields off
          to skip them; edit values inline before applying.
        </Typography>
        {draft.parsing_notes && (
          <Alert severity="info" icon={false}>
            {humanizeFieldRefs(draft.parsing_notes)}
          </Alert>
        )}
        {draft.ambiguities.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight="bold">
              Ambiguities
            </Typography>
            {draft.ambiguities.map((a, i) => (
              <Typography key={i} variant="caption" display="block">
                {humanizeFieldRefs(a)}
              </Typography>
            ))}
          </Alert>
        )}
        {draft.failed_sources.length > 0 && (
          <Alert severity="warning">
            <Typography variant="body2" fontWeight="bold">
              Failed sources
            </Typography>
            {draft.failed_sources.map((f, i) => (
              <Typography key={i} variant="caption" display="block">
                {f.name} — {f.status}: {humanizeFieldRefs(f.error)}
              </Typography>
            ))}
          </Alert>
        )}
      </Stack>
    )
  }

  const renderProposedRound = (round: CollatedFundingRound, idx: number) => {
    const toggledOn = roundToggles[idx] ?? true
    const expanded = quotesExpanded[`round_${idx}`] ?? false
    const edited = editedRounds[idx] ?? {
      application_open_date: round.application_open_date,
      application_close_date: round.application_close_date,
      decision_date: round.decision_date,
    }
    const updateEdit = (patch: Partial<DraftFundingRoundShape>) =>
      setEditedRounds((prev) => ({
        ...prev,
        [idx]: { ...edited, ...patch },
      }))
    const toDate = (s: string | null): Date | null => (s ? new Date(s) : null)
    const openValidation = validateOpenDate(
      toDate(edited.application_open_date),
      toDate(edited.application_close_date),
    )
    const closeValidation = validateCloseDate(
      toDate(edited.application_close_date),
      toDate(edited.application_open_date),
    )
    return (
      <Box
        key={idx}
        sx={{
          opacity: toggledOn ? 1 : 0.5,
          borderLeft: (t) =>
            `3px solid ${
              toggledOn ? t.palette.primary.main : t.palette.divider
            }`,
          pl: 2,
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Checkbox
            checked={toggledOn}
            onChange={(e) =>
              setRoundToggles((prev) => ({ ...prev, [idx]: e.target.checked }))
            }
            inputProps={{ 'aria-label': `Apply round ${idx + 1}` }}
            sx={{ mt: 1 }}
          />
          <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
            <TextField
              label="Open"
              type="date"
              size="small"
              value={edited.application_open_date ?? ''}
              onChange={(e) =>
                updateEdit({ application_open_date: e.target.value || null })
              }
              InputLabelProps={{ shrink: true }}
              error={openValidation !== true}
              helperText={openValidation !== true ? openValidation : undefined}
              fullWidth
            />
            <TextField
              label="Close"
              type="date"
              size="small"
              value={edited.application_close_date ?? ''}
              onChange={(e) =>
                updateEdit({ application_close_date: e.target.value || null })
              }
              InputLabelProps={{ shrink: true }}
              error={closeValidation !== true}
              helperText={
                closeValidation !== true ? closeValidation : undefined
              }
              fullWidth
            />
            <TextField
              label="Decision"
              type="date"
              size="small"
              value={edited.decision_date ?? ''}
              onChange={(e) =>
                updateEdit({ decision_date: e.target.value || null })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </Stack>
        {round.quotes.length > 0 && (
          <Box
            sx={{
              mt: 1,
              pl: 1.5,
              borderLeft: (t) => `2px solid ${t.palette.divider}`,
            }}
          >
            {renderQuoteList(round.quotes, expanded, `round_${idx}`)}
          </Box>
        )}
      </Box>
    )
  }

  const renderRoundsBlock = () => {
    if (!draft || draft.proposed_rounds.length === 0) return null
    return (
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1, mt: 1 }}>
          Proposed Funding Rounds
        </Typography>
        <Stack spacing={1.5}>
          {draft.proposed_rounds.map((r, idx) => renderProposedRound(r, idx))}
        </Stack>
      </Box>
    )
  }

  const renderReviewState = () => (
    <Box>
      {renderReviewHeader()}
      <Stack spacing={1.5}>
        {populatedFields.map((spec) => (
          <Box key={spec.name}>
            {renderFieldRow(spec)}
            {spec.name === 'timing' && renderRoundsBlock()}
          </Box>
        ))}
        {/* If timing wasn't populated, render the rounds block at the end so
            it still shows up. */}
        {!populatedFields.some((s) => s.name === 'timing') &&
          renderRoundsBlock()}
      </Stack>
    </Box>
  )

  // ──────────────────────────────────────────────────────────────────────
  // Root
  // ──────────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Auto-fill from URL or document</DialogTitle>
      <DialogContent dividers>
        {phase === 'input' && renderInputState()}
        {phase === 'pending' && renderPendingState()}
        {phase === 'review' && renderReviewState()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {phase === 'input' && (
          <Button
            variant="contained"
            onClick={handleExtract}
            disabled={!canExtract}
          >
            Extract
          </Button>
        )}
        {phase === 'review' && (
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={hasInvalidActiveRound}
            title={
              hasInvalidActiveRound
                ? 'Fix invalid round dates before applying'
                : undefined
            }
          >
            Apply to form
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
